import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Reveal } from "@/components/Reveal";
import { PriceReveal } from "@/components/PriceReveal";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Search, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { searchClaimableProviders } from "@/server/claim.functions";
import { brandedTitle, safeDesc } from "@/lib/seo-meta";

const SITE = "https://heallexa.com";

const META_TITLE = brandedTitle("List Your Practice | Free & Premium Plans");
const META_DESC = safeDesc(
  "Get your practice discovered on Heallexa. Free and Premium listing plans for doctors, dentists, therapists, and clinics. Claim your profile and accept appointment requests."
);

export const Route = createFileRoute("/list-your-practice")({
 head: () => ({
 meta: [
 { title: META_TITLE },
 { name: "description", content: META_DESC },
 { property: "og:title", content: META_TITLE },
 { property: "og:description", content: META_DESC },
 { property: "og:url", content: `${SITE}/list-your-practice` },
 { property: "og:type", content: "website" },
 { name: "twitter:title", content: META_TITLE },
 { name: "twitter:description", content: META_DESC },
 ],
 links: [{ rel: "canonical", href: `${SITE}/list-your-practice` }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "HowTo",
 name: "How to List Your Practice on Heallexa",
 description: "Claim or add your healthcare practice on Heallexa in minutes. Choose a Free or Premium plan and start receiving appointment requests.",
 totalTime: "PT5M",
 step: [
 {
 "@type": "HowToStep",
 position: 1,
 name: "Search for your practice",
 text: "Enter your practice name, NPI number, license number, city, or address in the search bar to find your existing Heallexa listing.",
 },
 {
 "@type": "HowToStep",
 position: 2,
 name: "Verify ownership",
 text: "Confirm you are the owner of the practice by verifying with your email address or practice credentials.",
 },
 {
 "@type": "HowToStep",
 position: 3,
 name: "Edit your listing",
 text: "Update your practice details including specialties, accepted insurance, services, languages spoken, photos, and appointment availability.",
 },
 {
 "@type": "HowToStep",
 position: 4,
 name: "Choose plan & go live",
 text: "Select a Free listing or upgrade to Premium starting at $29/month for priority placement, a verified badge, online booking, and performance analytics.",
 },
 ],
 }),
 },
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: SITE },
 { "@type": "ListItem", position: 2, name: "List Your Practice", item: `${SITE}/list-your-practice` },
 ],
 }),
 },
 ],
 }),
 component: ListPage,
});

function ListPage() {
 const { t } = useI18n();
 const navigate = useNavigate();
 const [billing, setBilling] = useState<"monthly" | "yearly">("yearly");
 const yearlySavings = 29 * 12 - 99;
 const yearlyDiscountPct = Math.round((yearlySavings / (29 * 12)) * 100);
 const plansRef = useRef<HTMLDivElement | null>(null);

 const runSearch = useServerFn(searchClaimableProviders);
 const [q, setQ] = useState("");
 const [searching, setSearching] = useState(false);
 const [results, setResults] = useState<any[] | null>(null);
 const [searched, setSearched] = useState(false);

 useEffect(() => {
   const term = q.trim();
   if (term.length < 2) { setResults(null); setSearched(false); return; }
   setSearching(true);
   const handle = setTimeout(async () => {
     try {
       const res = await runSearch({ data: { q: term } });
       setResults(res.results);
       setSearched(true);
     } catch { setResults([]); setSearched(true); }
     finally { setSearching(false); }
   }, 350);
   return () => clearTimeout(handle);
 }, [q, runSearch]);

 const scrollToPlans = () => plansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

 return (
 <div className="min-h-screen bg-background pb-1">
 <Navbar />

 <section className="pt-32 md:pt-36 pb-16 border-b border-border">
 <div className="mx-auto max-w-[820px] px-6">
 <div className="text-center mb-8">
 <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Already listed?</p>
 <h2 className="text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">Find your profile to claim it</h2>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-[520px] mx-auto">Search by name, practice, NPI, license number, city, or address.</p>
 </div>
 <div className="card-surface-flat p-2 rounded-md">
 <label className="relative flex items-center gap-2 px-3 h-11 border border-transparent focus-within:border-border bg-background rounded-md">
 <Search className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
 <input
 type="search"
 value={q}
 onChange={(e) => setQ(e.target.value)}
 placeholder="e.g. Jane Smith MD, 1234567890, San Diego"
 className="w-full bg-transparent text-[14px] text-foreground placeholder:text-muted-foreground outline-none"
 aria-label="Search for your provider profile"
 />
 {searching && (
 <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
 )}
 </label>
 </div>

 {results && results.length > 0 && (
 <ul className="mt-4 card-surface-flat rounded-md divide-y divide-border overflow-hidden">
 {results.map((p) => {
   const claimed = !!p.claimed_by;
   return (
     <li key={p.id} className="flex items-center gap-3 p-4">
       <div className="flex-1 min-w-0">
         <div className="text-sm font-semibold text-foreground truncate">
           {p.practice || p.name}
         </div>
         <div className="text-xs text-muted-foreground truncate">
           {[p.name !== p.practice ? p.name : null, p.specialty, [p.city, p.state].filter(Boolean).join(", "), p.npi ? `NPI ${p.npi}` : null]
             .filter(Boolean).join(" · ")}
         </div>
       </div>
       {claimed ? (
         <span className="inline-flex items-center gap-1 text-xs text-muted-foreground border border-border rounded-md px-2 py-1">
           <CheckCircle2 className="h-3 w-3" /> Already claimed
         </span>
       ) : (
         <Button asChild size="sm">
           <Link to="/claim" search={{ providerId: p.id, plan: "free" } as any}>Claim this</Link>
         </Button>
       )}
     </li>
   );
 })}
 </ul>
 )}

 {searched && results && results.length === 0 && (
 <div className="mt-4 card-surface p-10 text-center rounded-md">
 <p className="text-[14px] text-foreground font-medium">No matching listing found.</p>
 <p className="mt-1 text-[13px] text-muted-foreground">Add your practice to Heallexa in under a minute.</p>
 <button
 type="button"
 onClick={scrollToPlans}
 className="mt-6 inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-md"
 >
 List my practice
 </button>
 </div>
 )}

 <div className="mt-4 flex items-center justify-center gap-2 text-[12px] text-muted-foreground">
 <span>Don't see your practice?</span>
 <button type="button" onClick={scrollToPlans} className="text-primary font-medium hover:underline">
   Add a new listing →
 </button>
 </div>
 </div>
 </section>



 <section ref={plansRef} className="mx-auto max-w-[1200px] px-6 py-16 sm:py-24">
 <div className="mx-auto mb-8 grid max-w-md grid-cols-2 gap-2" role="group" aria-label="Choose premium billing cycle">
 <button
 type="button"
 onClick={() => setBilling("monthly")}
 aria-pressed={billing === "monthly"}
 className={`min-h-14 border rounded-md px-4 py-3 text-left transition-all ${
 billing === "monthly"
 ? "border-primary bg-primary text-primary-foreground shadow-sm"
 : "border-border bg-card text-foreground hover:border-primary/50"
 }`}
 >
 <span className="block text-sm font-semibold">Monthly</span>
 <span className={billing === "monthly" ? "block text-xs text-primary-foreground/80" : "block text-xs text-muted-foreground"}>$29/mo <span className="line-through opacity-70 ml-1">$99</span></span>
 </button>
 <button
 type="button"
 onClick={() => setBilling("yearly")}
 aria-pressed={billing === "yearly"}
 className={`min-h-14 border rounded-md px-4 py-3 text-left transition-all ${
 billing === "yearly"
 ? "border-primary bg-primary text-primary-foreground shadow-sm"
 : "border-border bg-card text-foreground hover:border-primary/50"
 }`}
 >
 <span className="block text-sm font-semibold">Yearly</span>
 <span className={billing === "yearly" ? "block text-xs text-primary-foreground/80" : "block text-xs text-muted-foreground"}>Save {yearlyDiscountPct}% · $99/yr <span className="line-through opacity-70 ml-1">$999</span></span>
 </button>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto items-stretch">
 <Reveal from="up" delay={0} className="h-full">
 <Plan
 name="Free Listing"
 price="$0"
 subtitle="Always free"
 features={["Practice name", "Phone number", "Address", "Website link", "Listed in Browse & Search"]}
 plan="free"
 ctaLabel="Claim free listing"
 ctaVariant="outline"
 />
 </Reveal>
 <Reveal from="up" delay={70} className="h-full">
 <Plan
 name="Premium Listing"
 badge="Promotional offer"
 price={billing === "yearly" ? "$99" : "$29"}
 strikePrice={billing === "yearly" ? "$999" : "$99"}
 subtitle={billing === "yearly" ? `per year, billed annually · save $${yearlySavings}` : "per month, billed monthly"}
 features={[
 "Everything in Free",
 "Verified badge",
 "Priority placement in results",
 "Photos & logo",
 "List of services",
 "Insurance accepted",
 "Online booking link",
 "Performance analytics",
 "Featured 3 days, twice a month, in Top Rated Doctors in your city",
 ]}
 plan={billing === "yearly" ? "premium_yearly" : "premium_monthly"}
 ctaLabel="Start Premium"
 highlight
 />
 </Reveal>
 </div>
 </section>

 <section className="bg-card border-y border-border py-16 sm:py-24">
 <div className="mx-auto max-w-[1200px] px-6">
 <Reveal from="up" delay={60}>
 <div className="text-center mb-12">
 <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">For providers</p>
 <h2 className="text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">How claiming works</h2>
 </div>
 </Reveal>
 <ol className="grid grid-cols-1 md:grid-cols-4 gap-6">
 {[
 "Search for your practice",
 "Verify ownership",
 "Edit your listing",
 "Choose plan & go live",
 ].map((step, i) => (
 <Reveal key={step} as="li" from="up" delay={Math.min(i, 8) * 70} className="card-surface-flat rounded-md p-6">
 <span className="text-[11px] font-semibold text-primary tracking-[0.18em] uppercase tabular-nums">{String(i + 1).padStart(2, "0")}</span>
 <p className="mt-3 text-[14px] font-medium text-foreground">{step}</p>
 </Reveal>
 ))}
 </ol>
 </div>
 </section>

 <FooterSection />
 </div>
 );
}

function Plan({ name, price, strikePrice, subtitle, features, plan, ctaLabel, ctaVariant, highlight, badge }: {
 name: string; price: string; strikePrice?: string; subtitle: string; features: string[]; plan: string; ctaLabel: string; ctaVariant?: "default" | "outline"; highlight?: boolean; badge?: string;
}) {
 const navigate = useNavigate();
 const signupPlan = plan;
 const go = () => navigate({ to: "/signup", search: { plan: signupPlan } as any });
 return (
 <div
 role="button"
 tabIndex={0}
 onClick={go}
 onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
 className={`relative cursor-pointer card-surface hover-lift rounded-md p-6 sm:p-7 h-full flex flex-col transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${highlight ? "border-primary" : ""}`}
 >
 {badge && (
 <span className="absolute top-4 right-4 rounded-full bg-primary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-foreground">{badge}</span>
 )}
 <h3 className="text-[11px] font-semibold text-primary tracking-[0.18em] uppercase">{name}</h3>
 <div className="mt-3">
 {strikePrice
 ? <PriceReveal oldPrice={strikePrice} newPrice={price} />
 : <span className="text-[36px] font-medium tracking-tight text-foreground">{price}</span>}
 </div>
 <p className="text-[13.5px] text-muted-foreground">{subtitle}</p>
 <ul className="mt-6 space-y-2.5">
 {features.map((f) => (
 <li key={f} className="flex items-start gap-2 text-[13.5px] text-foreground">
 <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f}
 </li>
 ))}
 </ul>
 <div className="mt-auto pt-8">
 <Button variant={ctaVariant ?? "default"} className="w-full" onClick={(e) => { e.stopPropagation(); go(); }}>
 {ctaLabel}
 </Button>
 </div>
 </div>
 );
}
